// Aba "Atividades" — visão Gantt (timeline horizontal por escala).
// Wave 2 (Antd) — 2026-06-01. Timeline core mantida custom (Antd não tem Gantt
// nativo); só wrappers/toolbar/legend usam Antd.

import { useEffect, useState } from 'react';
import { Segmented, Typography, Flex, Space, Empty } from 'antd';
import type { Atividade } from './types';

// ─── Gantt das atividades ────────────────────────────────
// Renderiza cada atividade como barra horizontal posicionada no eixo do
// tempo (data_inicio_prevista → data_termino_previsto). Cor da barra
// reflete o status, e o preenchimento interno mostra o % de progresso.
// Click numa barra abre a atividade pra edição. Linha vermelha vertical
// marca "hoje".
export function GanttAtividades({ atividades, onEditar }: {
  atividades: Atividade[];
  onEditar: (a: Atividade) => void;
}) {
  type Escala = 'dias' | 'semanas' | 'meses';
  const [escala, setEscala] = useState<Escala>(() =>
    (localStorage.getItem('op_gantt_escala') as Escala) || 'semanas'
  );
  useEffect(() => { localStorage.setItem('op_gantt_escala', escala); }, [escala]);

  // Atividades com pelo menos início OU fim previsto. Sem datas, fica "Sem prazo"
  // (não renderiza na timeline mas mostra na coluna de nomes pra completude).
  const comDatas = atividades.filter(a => a.data_inicio_prevista || a.data_termino_previsto);
  const semDatas = atividades.filter(a => !a.data_inicio_prevista && !a.data_termino_previsto);

  if (atividades.length === 0) return <Empty description="Sem atividades." />;

  // Range do timeline: min(inicio) → max(fim). Se faltam datas, usa hoje.
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const dts = (s: string | null | undefined): Date | null => {
    if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
    const d = new Date(s.slice(0, 10) + 'T12:00:00');
    return Number.isFinite(d.getTime()) ? d : null;
  };
  let minDate = new Date(hoje); let maxDate = new Date(hoje);
  for (const a of comDatas) {
    const ini = dts(a.data_inicio_prevista) || dts(a.data_termino_previsto)!;
    const fim = dts(a.data_termino_previsto) || dts(a.data_inicio_prevista)!;
    if (ini && ini < minDate) minDate = ini;
    if (fim && fim > maxDate) maxDate = fim;
  }
  // Padding: 3 dias antes e 7 dias depois pra respiro
  minDate.setDate(minDate.getDate() - 3);
  maxDate.setDate(maxDate.getDate() + 7);

  // Px por dia conforme escala
  const PX_DIA: Record<Escala, number> = { dias: 32, semanas: 12, meses: 4 };
  const pxDia = PX_DIA[escala];

  const totalDias = Math.max(1, Math.round((maxDate.getTime() - minDate.getTime()) / 86400000));
  const totalWidth = totalDias * pxDia;
  const ROW_H = 28;
  const LEFT_W = 240;  // largura da coluna de nomes

  // Helpers de posição
  const dayOffset = (d: Date) => Math.round((d.getTime() - minDate.getTime()) / 86400000);

  // Gera os ticks do header conforme escala
  const ticks: { date: Date; label: string; major?: boolean }[] = [];
  const cursor = new Date(minDate);
  if (escala === 'dias') {
    while (cursor <= maxDate) {
      ticks.push({
        date: new Date(cursor),
        label: cursor.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
        major: cursor.getDay() === 1,  // segunda
      });
      cursor.setDate(cursor.getDate() + 1);
    }
  } else if (escala === 'semanas') {
    // Snap pra segunda-feira
    while (cursor.getDay() !== 1) cursor.setDate(cursor.getDate() - 1);
    while (cursor <= maxDate) {
      ticks.push({
        date: new Date(cursor),
        label: cursor.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
        major: cursor.getDate() <= 7,  // primeira semana do mês
      });
      cursor.setDate(cursor.getDate() + 7);
    }
  } else {
    // meses
    cursor.setDate(1);
    while (cursor <= maxDate) {
      ticks.push({
        date: new Date(cursor),
        label: cursor.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        major: true,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
  }

  const todayLeft = dayOffset(hoje) * pxDia;

  // Cores por status (mesma escala do kanban)
  const corStatus: Record<string, string> = {
    pendente: '#888', em_andamento: '#0930D0', concluida: '#0a7f4a',
    bloqueada: '#B91626', cancelada: '#aaa',
  };

  function renderBarra(a: Atividade, idx: number) {
    const ini = dts(a.data_inicio_prevista) || dts(a.data_termino_previsto);
    const fim = dts(a.data_termino_previsto) || dts(a.data_inicio_prevista);
    if (!ini || !fim) return null;
    const left = dayOffset(ini) * pxDia;
    const dur = Math.max(1, Math.round((fim.getTime() - ini.getTime()) / 86400000) + 1);
    const width = dur * pxDia;
    const cor = corStatus[a.status] || '#888';
    const pct = Math.min(100, Math.max(0, a.progresso_pct || 0));
    return (
      <div key={a.id} title={`${a.titulo} · ${a.status} · ${pct}%`}
        onClick={() => onEditar(a)}
        style={{
          position: 'absolute', top: idx * ROW_H + 5, left, width,
          height: ROW_H - 10, background: cor, borderRadius: 4,
          cursor: 'pointer', overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', paddingLeft: 6,
          fontSize: 10, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap',
        }}>
        {pct > 0 && (
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${pct}%`, background: 'rgba(255,255,255,0.25)',
          }} />
        )}
        <span style={{ position: 'relative', zIndex: 1, textOverflow: 'ellipsis',
          overflow: 'hidden', maxWidth: '100%' }}>
          {pct > 0 ? `${pct}% · ` : ''}{a.titulo}
        </span>
      </div>
    );
  }

  return (
    <div>
      <Flex align="center" gap={8} style={{ marginBottom: 10 }}>
        <Typography.Text strong style={{ fontSize: 11, color: '#666' }}>Escala:</Typography.Text>
        <Segmented size="small" value={escala} onChange={(v) => setEscala(v as Escala)}
          options={[
            { label: 'Dias', value: 'dias' },
            { label: 'Semanas', value: 'semanas' },
            { label: 'Meses', value: 'meses' },
          ]} />
        <div style={{ flex: 1 }} />
        <Typography.Text type="secondary" style={{ fontSize: 10 }}>
          {comDatas.length} com prazo · {semDatas.length} sem prazo
        </Typography.Text>
      </Flex>

      {/* Container scrollable horizontal — coluna de nomes sticky à esquerda */}
      <div style={{
        border: '0.5px solid #e0e0e0', borderRadius: 6, overflow: 'auto',
        maxHeight: '70vh', background: '#fff',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: `${LEFT_W}px 1fr`,
          minWidth: LEFT_W + totalWidth }}>
          {/* COLUNA ESQUERDA: nomes (sticky) */}
          <div style={{
            position: 'sticky', left: 0, zIndex: 2, background: '#FAFBFF',
            borderRight: '1px solid #ddd',
          }}>
            {/* Header */}
            <div style={{ height: 32, borderBottom: '1px solid #ddd',
              padding: '6px 10px', fontSize: 11, fontWeight: 600, color: '#020D7F',
              display: 'flex', alignItems: 'center' }}>
              Atividade
            </div>
            {comDatas.map((a) => (
              <div key={a.id} onClick={() => onEditar(a)}
                title={a.titulo}
                style={{
                  height: ROW_H, borderBottom: '0.5px solid #f0f0f0',
                  padding: '0 10px', fontSize: 11, color: '#222',
                  display: 'flex', alignItems: 'center', cursor: 'pointer',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                {a.titulo}
              </div>
            ))}
            {semDatas.length > 0 && (
              <>
                <div style={{ height: ROW_H, borderBottom: '0.5px solid #f0f0f0',
                  padding: '0 10px', fontSize: 10, color: '#aaa', fontStyle: 'italic',
                  display: 'flex', alignItems: 'center',
                  background: '#FFF8E8' }}>
                  ⚠ Sem prazo definido:
                </div>
                {semDatas.map(a => (
                  <div key={a.id} onClick={() => onEditar(a)} title={a.titulo}
                    style={{
                      height: ROW_H, borderBottom: '0.5px solid #f0f0f0',
                      padding: '0 10px', fontSize: 11, color: '#888',
                      display: 'flex', alignItems: 'center', cursor: 'pointer',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{a.titulo}</div>
                ))}
              </>
            )}
          </div>

          {/* COLUNA DIREITA: timeline */}
          <div style={{ position: 'relative', minHeight: comDatas.length * ROW_H + 32 }}>
            {/* Header com ticks */}
            <div style={{ height: 32, borderBottom: '1px solid #ddd',
              position: 'relative', background: '#FAFBFF' }}>
              {ticks.map((t, i) => (
                <div key={i} style={{
                  position: 'absolute', left: dayOffset(t.date) * pxDia,
                  top: 0, bottom: 0,
                  borderLeft: t.major ? '1px solid #B07500' : '1px solid #eee',
                  padding: '6px 4px', fontSize: 9,
                  color: t.major ? '#B07500' : '#888',
                  fontWeight: t.major ? 700 : 400, whiteSpace: 'nowrap',
                }}>{t.label}</div>
              ))}
            </div>
            {/* Linhas guia horizontais + verticais */}
            <div style={{ position: 'relative', height: comDatas.length * ROW_H }}>
              {/* Grid de linhas verticais */}
              {ticks.map((t, i) => (
                <div key={i} style={{
                  position: 'absolute', left: dayOffset(t.date) * pxDia,
                  top: 0, bottom: 0, width: 1,
                  background: t.major ? '#eee' : '#f5f5f5',
                }} />
              ))}
              {/* Linha de hoje */}
              {todayLeft >= 0 && todayLeft <= totalWidth && (
                <div style={{
                  position: 'absolute', left: todayLeft, top: 0, bottom: 0,
                  width: 2, background: '#B91626', zIndex: 1,
                }} title={`Hoje: ${hoje.toLocaleDateString('pt-BR')}`} />
              )}
              {/* Linhas horizontais (ALT row tinting) */}
              {comDatas.map((a, i) => (
                <div key={a.id} style={{
                  position: 'absolute', top: i * ROW_H, left: 0, right: 0,
                  height: ROW_H, borderBottom: '0.5px solid #f0f0f0',
                  background: i % 2 === 0 ? '#fff' : '#FAFBFF',
                }} />
              ))}
              {/* Barras das atividades */}
              {comDatas.map((a, i) => renderBarra(a, i))}
            </div>
          </div>
        </div>
      </div>

      <Flex gap={12} wrap style={{ marginTop: 10 }}>
        {Object.entries(corStatus).map(([s, c]) => (
          <Space key={s} size={4}>
            <span style={{ width: 12, height: 12, background: c, borderRadius: 2, display: 'inline-block' }} />
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>{s.replace('_', ' ')}</Typography.Text>
          </Space>
        ))}
        <Space size={4}>
          <span style={{ width: 2, height: 14, background: '#B91626', display: 'inline-block' }} />
          <Typography.Text type="secondary" style={{ fontSize: 10 }}>hoje</Typography.Text>
        </Space>
      </Flex>
    </div>
  );
}
